import { ChevronRight, ExternalLink, Settings } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';

import { LinkCard, PageHeader } from '../design/components.js';

import { useShell } from './Shell.js';
import { iconOf } from './icons.js';
import {
  domaineInfo,
  domainActions,
  entreeDe,
  VUE_CARTES,
  domainTodo,
  hubOf,
  moduleSettings,
  SETTINGS_PATH,
  type Domaine,
  type HubBloc,
  type NavEntry,
  type ShellAction,
  type ShellTile,
} from './navigation.js';

import './hub.css';

/**
 * Page de domaine générique (maquette validée, docs/ux/navigation.md) : titre et phrase à
 * gauche, à droite « Réglages » puis 1 à 3 boutons d'action (le principal, orange, tout à
 * droite) ; bandeau « À traiter » en une ligne ; puis les cartes en trois colonnes « Pour
 * moi », « Gérer », « Piloter ». Tout vient de la coque calculée par le serveur : aucun écran
 * n'est ajouté.
 */

const isExternal = (path: string): boolean => path.startsWith('https://');

/** Gros bouton d'action : le premier est le bouton principal (orange), l'urgence en rouge. */
export function ActionButton({
  action,
  primary = false,
  big = false,
}: {
  action: ShellAction;
  primary?: boolean;
  big?: boolean;
}): ReactElement {
  const Icon = iconOf(action.icon);
  const classes = ['o-button'];
  if (action.danger === true) classes.push('o-button--danger');
  else if (primary) classes.push('o-button--primary');
  if (big) classes.push('o-hub-action');
  const content = (
    <>
      <Icon aria-hidden="true" size={18} />
      <span>{action.label}</span>
      {isExternal(action.path) && <ExternalLink aria-hidden="true" size={14} />}
    </>
  );
  return isExternal(action.path) ? (
    <a className={classes.join(' ')} href={action.path} target="_blank" rel="noreferrer noopener">
      {content}
    </a>
  ) : (
    <Link className={classes.join(' ')} to={action.path}>
      {content}
    </Link>
  );
}

/**
 * Tuile d'accueil (gros bouton carré) : interne ou externe (nouvel onglet). La première de
 * l'accueil est pleine, orange foncé (`primary`) : c'est ce que l'on fait le plus souvent.
 */
export function HomeTile({
  to,
  label,
  icon,
  hint,
  primary = false,
}: {
  to: string;
  label: string;
  icon: string | undefined;
  hint?: string | undefined;
  primary?: boolean;
}): ReactElement {
  const Icon = iconOf(icon);
  const className = primary ? 'o-home-tile o-home-tile--primary' : 'o-home-tile';
  const body = (
    <>
      <span className="o-home-tile__icon" aria-hidden="true">
        <Icon size={26} />
      </span>
      <span className="o-home-tile__label">{label}</span>
      {hint !== undefined && <span className="o-home-tile__hint">{hint}</span>}
    </>
  );
  return isExternal(to) ? (
    <a className={className} href={to} target="_blank" rel="noreferrer noopener">
      {body}
    </a>
  ) : (
    <Link className={className} to={to}>
      {body}
    </Link>
  );
}

/** Valeur affichée d'un compteur. */
export const tileValue = (t: ShellTile): string =>
  typeof t.value === 'number' || typeof t.value === 'string' ? String(t.value) : '';

/**
 * Bandeau « À traiter » d'une page de domaine, en une ligne : « 3 emprunts non rendus · 1
 * contrôle technique dépassé », chaque élément menant à sa liste filtrée.
 */
export function TodoStrip({ tiles }: { tiles: readonly ShellTile[] }): ReactElement {
  return (
    <section className="o-hub-strip" aria-labelledby="a-traiter">
      <h2 id="a-traiter" className="o-hub-strip__title">
        À traiter :
      </h2>
      <ul className="o-hub-strip__list">
        {tiles.map((t) => {
          const text = `${tileValue(t)} ${t.label}`;
          return (
            <li
              key={`${t.module}:${t.key}`}
              className={`o-hub-strip__item--${t.severity ?? 'info'}`}
            >
              {t.link === undefined ? text : <Link to={t.link}>{text}</Link>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** Liste de compteurs cliquables (« À faire maintenant »). */
export function TodoList({
  tiles,
  label,
}: {
  tiles: readonly ShellTile[];
  label: string;
}): ReactElement {
  return (
    <ul className="o-todo" aria-label={label}>
      {tiles.map((t) => {
        const body = (
          <>
            <span
              className={`o-todo__dot o-todo__dot--${t.severity ?? 'info'}`}
              aria-hidden="true"
            />
            <span className="o-todo__value">{tileValue(t)}</span>
            <span className="o-todo__label">{t.label}</span>
            {t.link !== undefined && (
              <ChevronRight aria-hidden="true" size={16} className="o-todo__chevron" />
            )}
          </>
        );
        return (
          <li key={`${t.module}:${t.key}`}>
            {t.link === undefined ? (
              <span className="o-todo__row">{body}</span>
            ) : (
              <Link className="o-todo__row" to={t.link}>
                {body}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Blocs de cartes d'une page de domaine, en colonnes (une au téléphone). */
export function HubBlocs({
  blocs,
  dense = false,
  children,
}: {
  blocs: readonly HubBloc[];
  /** Blocs en colonnes remplies l'une après l'autre, sans trou (page « Plus »). */
  dense?: boolean;
  /** Blocs supplémentaires rangés avec les autres (liens utiles). */
  children?: ReactNode;
}): ReactElement {
  return (
    <div className={dense ? 'o-hub-blocs o-hub-blocs--dense' : 'o-hub-blocs'}>
      {blocs.map((b) => (
        <section key={b.key} className="o-hub-bloc" aria-labelledby={`bloc-${b.key}`}>
          <h2 id={`bloc-${b.key}`} className="o-hub-bloc__title">
            {b.title}
          </h2>
          <div className="o-hub-cards">
            {b.cards.map((c) => (
              <LinkCard
                key={`${c.label}:${c.path}`}
                title={c.label}
                description={c.description}
                icon={iconOf(c.icon)}
                to={c.path}
                links={c.tabs.map((t) => ({ to: t.path, label: t.label }))}
              />
            ))}
          </div>
        </section>
      ))}
      {children}
    </div>
  );
}

/**
 * Lien « Réglages » d'un domaine : la page de réglages du module concerné, ou le catalogue
 * si plusieurs modules du domaine ont des réglages permis.
 */
export function settingsLink(domaine: Domaine, entries: readonly NavEntry[]): string | undefined {
  const modules = new Set(
    entries.filter((e) => e.domaine === domaine && e.available !== false).map((e) => e.module),
  );
  const pages = moduleSettings(entries).filter((m) => m.tabs.some((t) => modules.has(t.module)));
  if (pages.length === 0) return undefined;
  return pages.length === 1 ? pages[0]?.path : SETTINGS_PATH;
}

/** En-tête commun des pages de domaine : titre, phrase, Réglages, boutons d'action. */
export function DomainHeader({
  domaine,
  extra,
}: {
  domaine: Domaine;
  extra?: ReactNode;
}): ReactElement {
  const { shell } = useShell();
  const info = domaineInfo(domaine);
  const actions = domainActions(domaine, shell.actions);
  const settings =
    domaine === 'administration' ? undefined : settingsLink(domaine, shell.navigation);
  // Ordre visuel constant : Réglages, actions secondaires, puis le principal tout à droite.
  const [main, ...others] = actions;
  const buttons =
    settings === undefined && actions.length === 0 ? undefined : (
      <div className="o-hub-actions">
        {settings !== undefined && (
          <Link className="o-button o-button--ghost" to={settings}>
            <Settings aria-hidden="true" size={18} /> Réglages
          </Link>
        )}
        {others.map((a) => (
          <ActionButton key={a.key} action={a} />
        ))}
        {main !== undefined && <ActionButton action={main} primary />}
      </div>
    );
  return (
    <>
      <PageHeader
        title={info?.label ?? domaine}
        description={info?.phrase ?? ''}
        actions={buttons}
      />
      {extra}
    </>
  );
}

/**
 * Page de rubrique. Une rubrique qui déclare une page d'entrée (module.json `entree`) s'ouvre
 * directement sur elle quand elle est permise ; `?vue=cartes` affiche toujours les cartes.
 */
export function DomainPage({ domaine }: { domaine: Domaine }): ReactElement {
  const { shell } = useShell();
  const [params] = useSearchParams();
  const entree = entreeDe(domaine, shell.navigation);
  if (entree !== undefined && params.get('vue') !== VUE_CARTES)
    return <Navigate to={entree.path} replace />;
  return <DomainCards domaine={domaine} />;
}

function DomainCards({ domaine }: { domaine: Domaine }): ReactElement {
  const { shell } = useShell();
  const blocs = hubOf(domaine, shell.navigation);
  const todo = domainTodo(domaine, shell.tiles);
  return (
    <div className="o-stack o-hub">
      <DomainHeader domaine={domaine} />
      {todo.length > 0 && <TodoStrip tiles={todo} />}
      {blocs.length === 0 ? (
        <p className="o-hint">Votre profil ne donne accès à aucun écran de cette rubrique.</p>
      ) : (
        <HubBlocs blocs={blocs} />
      )}
    </div>
  );
}
